/**
 * TemplatePicker Component
 *
 * Dropdown picker for selecting a template from the user's available templates.
 * Uses V2 templates API but maintains backward-compatible callback interface.
 *
 * MIGRATION NOTE:
 * - Internally uses V2 API (useGetTemplatesQuery)
 * - Callback still receives legacy-compatible shape for backward compatibility
 * - Once all consumers are migrated, callback type can be updated
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useGetTemplatesQuery } from '@features/templates';
import { useEventTracking } from '@services/statsig/hooks';
import { PushPin } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Template item for picker selection
 *
 * This interface maintains backward compatibility with legacy consumers
 * while supporting V2 API data. Properties use legacy naming convention.
 */
export interface TemplatePickerItem {
  /** Template ID (legacy naming for backward compatibility) */
  template_id: string;
  /** Display name (legacy naming for backward compatibility) */
  display_name: string;
  /** Template type: 'user' | 'public' (legacy naming for backward compatibility) */
  type: 'user' | 'public';
  /** Owner scope from V2 API (new field) */
  owner_scope?: 'user' | 'org' | 'public';
  /** Display order for pinned templates (0-4 = pinned, null = unpinned) */
  displayOrder?: number | null;
}

interface TemplatePickerProps {
  /** Callback when template is selected */
  onTemplateSelect: (template: TemplatePickerItem | undefined) => void;
  /** Initial template selection */
  initialTemplate?: TemplatePickerItem;
  /** Initial template ID to select */
  initialTemplateId?: string | null;
  /** Whether selection is required */
  required?: boolean;
  /** Input label text */
  label?: string;
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  onTemplateSelect,
  initialTemplate,
  initialTemplateId,
  required = false,
  label = 'Choose a template',
}) => {
  const { data: templatesData, isLoading } = useGetTemplatesQuery();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplatePickerItem | undefined>(
    initialTemplate,
  );
  const { template: templateMetrics } = useEventTracking();

  // Transform V2 templates to legacy-compatible format and sort by pin status
  const templatesList = useMemo<TemplatePickerItem[]>(() => {
    if (!templatesData?.templates) {
      return [];
    }

    const templates = templatesData.templates.map(item => ({
      template_id: item.template.id,
      display_name: item.revision.title,
      // Map owner_scope to legacy 'type' field for backward compatibility
      type: (item.template.owner_scope === 'user' ? 'user' : 'public') as 'user' | 'public',
      owner_scope: item.template.owner_scope,
      displayOrder: item.display_order ?? null,
    }));

    // Sort: pinned templates first (by displayOrder), then unpinned alphabetically
    return templates.sort((a, b) => {
      const aIsPinned = a.displayOrder !== null && a.displayOrder !== undefined;
      const bIsPinned = b.displayOrder !== null && b.displayOrder !== undefined;

      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      if (aIsPinned && bIsPinned) return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      return a.display_name.localeCompare(b.display_name);
    });
  }, [templatesData]);

  const handleTemplateChange = (_event: React.SyntheticEvent, value: TemplatePickerItem | null) => {
    if (value) {
      setSelectedTemplate(value);
      onTemplateSelect(value);

      // Track template selection
      templateMetrics.select(value.template_id, {
        is_custom: value.type === 'user',
      });
    } else {
      setSelectedTemplate(undefined);
      onTemplateSelect(undefined);
    }
  };

  // Set initial template when initialTemplateId changes
  useEffect(() => {
    if (initialTemplateId && templatesList.length > 0) {
      const template = templatesList.find(t => t.template_id === initialTemplateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [initialTemplateId, templatesList]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl fullWidth>
        <Autocomplete
          options={templatesList}
          value={selectedTemplate || null}
          getOptionLabel={option => option.display_name || ''}
          isOptionEqualToValue={(option, value) => option.template_id === value.template_id}
          loading={isLoading}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              required={required}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const isPinned = option.displayOrder !== null && option.displayOrder !== undefined;
            return (
              <Box component="li" {...props} key={option.template_id}>
                {isPinned && <PushPin sx={{ fontSize: 16, color: 'primary.light', mr: 1 }} />}
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.display_name}
                </Typography>
              </Box>
            );
          }}
          onChange={handleTemplateChange}
        />
      </FormControl>
    </Box>
  );
};

export default TemplatePicker;

// Named export for more explicit imports
export { TemplatePicker };
